import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Users, Dumbbell, CheckCircle2, MessageSquareText, Inbox, Flame } from "lucide-react";
import { toast } from "sonner";
import { BRAND } from "@/lib/brand";

const KPIS = [
  { key: "new_leads", label: "New leads", icon: Inbox, link: "/coach/leads" },
  { key: "active_clients", label: "Active clients", icon: Users, link: "/coach/clients" },
  { key: "programs", label: "Programs", icon: Dumbbell, link: "/coach/programs" },
  { key: "checkins_today", label: "Rituals today", icon: CheckCircle2 },
  { key: "unread_messages", label: "Unread", icon: MessageSquareText, link: "/coach/messages" },
];

export default function CoachOverview() {
  const [data, setData] = useState({});
  const [fenyxLive, setFenyxLive] = useState(false);
  useEffect(() => {
    api.get("/coach/overview").then((r) => setData(r.data)).catch(() => {});
    api.get("/public/config").then((r) => setFenyxLive(!!r.data.fenyx_live)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only fetch; api is a stable module import
  }, []);

  const toggleFenyx = async () => {
    const next = !fenyxLive;
    try {
      await api.patch("/coach/settings", { fenyx_live: next });
      setFenyxLive(next);
      toast.success(next ? "FENYX checkout is LIVE on your landing page" : "FENYX back to waitlist mode");
    } catch { toast.error("Could not update"); }
  };

  return (
    <div className="space-y-14" data-testid="coach-overview">
      <header>
        <p className="overline">Welcome</p>
        <h1 className="font-display text-4xl sm:text-5xl mt-6 leading-tight">
          Good day,<br />
          <span className="font-script normal-case" style={{ fontSize: "1.15em", color: "var(--accent)", letterSpacing: 0 }}>Coach DJ.</span>
        </h1>
        <div className="bronze-rule mt-6" />
        <p className="font-soft text-xl mt-6 max-w-xl" style={{ color: "var(--text-secondary)" }}>
          A quiet view of {BRAND.method} — every woman in your care, at a glance.
        </p>
      </header>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-px" style={{ background: "var(--border-strong)" }}>
        {KPIS.map(({ key, label, icon: Icon, link }) => {
          const tile = (
            <div className="p-8 h-full" style={{ background: "var(--surface-1)" }} data-testid={`coach-kpi-${key}`}>
              <Icon size={14} style={{ color: "var(--accent)" }} />
              <div className="overline mt-6">{label}</div>
              <div className="font-display text-4xl mt-3">{data[key] ?? 0}</div>
            </div>
          );
          return link ? <Link key={key} to={link} className="block hover:bg-surface-2 transition-colors">{tile}</Link> : <div key={key}>{tile}</div>;
        })}
      </div>

      <div className="p-8 flex items-center justify-between flex-wrap gap-5" style={{ background: "var(--surface-1)", border: "1px solid var(--border-strong)" }} data-testid="fenyx-control-card">
        <div>
          <p className="overline flex items-center gap-2"><Flame size={13} style={{ color: "var(--accent)" }} /> FENYX Membership · $30/mo</p>
          <h3 className="font-display text-2xl mt-3">{fenyxLive ? "Checkout is LIVE on your landing page" : "Waitlist mode — checkout hidden"}</h3>
          <p className="font-soft text-base mt-2 max-w-xl" style={{ color: "var(--text-secondary)" }}>
            {fenyxLive
              ? "Visitors can join and pay instantly. New members get self-guided access only — no coaching features."
              : "Visitors see the founding waitlist. Preview the checkout anytime at /fenyx, then flip this switch when you're ready to launch."}
          </p>
        </div>
        <button onClick={toggleFenyx} data-testid="fenyx-live-toggle"
          className="px-6 py-3 font-label text-[10px] tracking-[0.2em] uppercase border transition-colors"
          style={{
            background: fenyxLive ? "var(--accent)" : "transparent",
            borderColor: "var(--accent)",
            color: fenyxLive ? "#FFFFFF" : "var(--accent)",
          }}>
          {fenyxLive ? "Live · Switch to waitlist" : "Go live"}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-px" style={{ background: "var(--border-strong)" }}>
        <div className="lg:col-span-2 p-10" style={{ background: "var(--surface-1)" }}>
          <p className="overline">Today&apos;s intention</p>
          <h2 className="font-display text-2xl sm:text-3xl mt-5 leading-tight">
            Three quiet touchpoints<br />
            <span className="font-script normal-case" style={{ fontSize: "1.15em", color: "var(--accent)", letterSpacing: 0 }}>change everything.</span>
          </h2>
          <div className="bronze-rule mt-6" />
          <p className="font-soft text-lg mt-6" style={{ color: "var(--text-secondary)" }}>
            Open <span className="text-accent">Messages</span> to send a quick note to your top three clients,
            then celebrate their habit check-ins. Small attention compounds.
          </p>
        </div>
        <div className="p-10" style={{ background: "var(--surface-1)" }}>
          <p className="overline">Quick links</p>
          <ul className="mt-6 space-y-4 font-label text-xs tracking-[0.18em] uppercase">
            <li><a className="hover:text-accent transition-colors" href="/coach/programs">→ Build a new program</a></li>
            <li><a className="hover:text-accent transition-colors" href="/coach/ai">→ Generate workout / meals</a></li>
            <li><a className="hover:text-accent transition-colors" href="/coach/exercises">→ Add to library</a></li>
            <li><a className="hover:text-accent transition-colors" href="/coach/resources">→ Share a resource</a></li>
          </ul>
        </div>
      </div>
    </div>
  );
}
